"use client";

// WhySection — Signal rebrand.
//
// The old shape lived in two parts:
//   1. An animated KPI counter bar (18 PoPs, 96+ BGP peers, 99.99% SLA,
//      7 countries) that ramped from 0 → target on scroll.
//   2. Four differentiator cards below, colored icon squares + text.
//
// Two problems the moodboard forced us to fix:
//   - The count-up dramatised static contract targets. 99.99% is a
//     contractual SLA, not a live measurement. Animating up to it performs
//     a measurement that never happened, same class of dishonesty as the
//     /status/ simulated telemetry. The counter is removed; the numbers
//     ship as-is in font-mono tabular type.
//   - The four "route tickets" moodboard pattern is a better fit for a
//     network operator than a KPI counter bar. The card is a datum, not a
//     progress bar. Each ticket carries a value + a mono label with the
//     accent hairline over the top border.
//
// Below the tickets the differentiator grid stays as four items (same copy
// via existing i18n keys) but shifts to the schematic-card pattern.

import { motion } from "framer-motion";
import { memo } from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "@/lib/i18n";
import {
  TOTAL_POP_COUNT,
  IP_TRANSIT_SLA_PERCENT,
  BGP_PEER_FLOOR,
  COUNTRIES_SERVED_COUNT,
} from "@/lib/network-metrics";

/**
 * Route tickets — four datapoints, static values, no animation.
 *
 * All four values are sourced from network-metrics constants so a change
 * to the PoP list or SLA tier updates the homepage automatically:
 *   • TOTAL_POP_COUNT     — derived from ALL_POP_IDS.length
 *   • BGP_PEER_FLOOR      — 96 (advertised floor, used with "+")
 *   • IP_TRANSIT_SLA_PERCENT — "99.99%"
 *   • COUNTRIES_SERVED_COUNT — distinct-country count from ALL_POPS
 */
const routeTickets = [
  {
    key: "pops",
    value: String(TOTAL_POP_COUNT),
    suffix: "",
    labelKey: "points_of_presence",
  },
  {
    key: "peers",
    value: String(BGP_PEER_FLOOR),
    suffix: "+",
    labelKey: "bgp_peers",
  },
  {
    key: "sla",
    // IP_TRANSIT_SLA_PERCENT is the site-wide "99.99%" figure; strip the
    // percent sign because the ticket renders it separately in --ink-dim.
    value: IP_TRANSIT_SLA_PERCENT.replace("%", ""),
    suffix: "%",
    labelKey: "uptime_sla",
  },
  {
    key: "countries",
    value: String(COUNTRIES_SERVED_COUNT),
    suffix: "",
    labelKey: "countries_kpi",
  },
] as const;

// Differentiator icons — kept identical to the pre-Signal file so nothing
// about the four sections' iconography changes; only their frame does.
const differentiatorIcons = [
  (
    <svg key="icon-location" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  ),
  (
    <svg key="icon-sparkle" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
    </svg>
  ),
  (
    <svg key="icon-terminal" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
    </svg>
  ),
  (
    <svg key="icon-clock" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
];

export const WhySection = memo(function WhySection() {
  const { t } = useTranslation();

  const differentiators = [
    {
      icon: differentiatorIcons[0],
      title: t("diff_strategic_location"),
      desc: t("diff_strategic_desc"),
    },
    {
      icon: differentiatorIcons[1],
      title: t("diff_ai_ready"),
      desc: t("diff_ai_desc"),
    },
    {
      icon: differentiatorIcons[2],
      title: t("diff_naas"),
      desc: t("diff_naas_desc"),
    },
    {
      icon: differentiatorIcons[3],
      title: t("diff_noc"),
      desc: t("diff_noc_desc"),
    },
  ];

  return (
    <section id="why-sofia-connect" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/*
          Route tickets — plain equal 2x2 (mobile) / 4x1 (≥md) grid.
          Per user directive: tickets stay a plain equal row; bento sizing
          reasoning applies only to service cards.
        */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20"
        >
          {routeTickets.map((ticket) => (
            <div key={ticket.key} className="signal-ticket text-center">
              {/* Value: font-mono tabular numerals, no animation. */}
              <div className="text-3xl sm:text-4xl font-mono tabular-nums text-text-primary">
                {ticket.value}
                {ticket.suffix ? (
                  <span className="text-[color:var(--signal-ink-dim)]">
                    {ticket.suffix}
                  </span>
                ) : null}
              </div>
              {/* Label: mono 10px uppercase, ink-dim. */}
              <div
                className="mt-2 font-mono text-[10px] uppercase text-[color:var(--signal-ink-dim)]"
                style={{ letterSpacing: "0.08em" }}
              >
                {t(ticket.labelKey)}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Section header — eyebrow lowercase mono, h2 in Unbounded. */}
        <div className="text-center mb-16">
          <span
            className="font-mono text-[11px] sm:text-[12px] lowercase text-eyebrow"
            style={{ letterSpacing: "0.08em" }}
          >
            {t("why_section_eyebrow")}
          </span>
          <h2
            className="font-display mt-3 text-3xl sm:text-4xl tracking-tight text-foreground"
            style={{ fontWeight: 600 }}
          >
            {t("why_section_heading")}
          </h2>
        </div>

        {/* Differentiators — schematic card frame, unchanged content. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {differentiators.map((d, i) => (
            <motion.div
              key={d.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5, ease: "easeOut" }}
              className="signal-sch-card"
            >
              <div className="relative z-10 flex gap-4">
                <div className="shrink-0 w-10 h-10 rounded-[2px] border border-[var(--signal-line)] text-[color:var(--accent)] flex items-center justify-center">
                  {d.icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-1">
                    {d.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {d.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Network fact highlight — kept, treated as a schematic card too. */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.45, duration: 0.5, ease: "easeOut" }}
          className="mt-12 max-w-3xl mx-auto text-center signal-sch-card"
        >
          <div className="text-2xl text-accent mb-3" aria-hidden="true">
            <svg className="w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {t("why_fact_text")}
          </p>
          <p className="mt-3 text-sm text-muted">{t("why_fact_as")}</p>
        </motion.div>

        {/* CTA — unchanged. */}
        <div className="mt-12 text-center">
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-border text-foreground rounded-[2px] hover:bg-surface-light transition-colors"
          >
            {t("experience_diff")}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
});
WhySection.displayName = "WhySection";
